// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/validation/ValidationIntelligencePanel.jsx

import React, { useMemo, useState } from "react";
import { Button, StatusChip } from "../../design-system/components";
import "./ValidationIntelligencePanel.css";

const PROMPTS = [
  "What needs attention now?",
  "Why are validations failing?",
  "What is blocking closure?",
  "What should I review first?",
];

function clean(value) {
  return String(value ?? "").trim();
}

function normalizeRows(value) {
  return Array.isArray(value) ? value : [];
}

function formatConfidence(value) {
  const normalized = clean(value).toLowerCase();

  if (normalized === "high") return "High confidence";
  if (normalized === "medium") return "Medium confidence";
  if (normalized === "low") return "Low confidence";
  return "Advisory";
}

function confidenceTone(value) {
  const normalized = clean(value).toLowerCase();

  if (normalized === "high") return "success";
  if (normalized === "medium") return "informational";
  if (normalized === "low") return "warning";
  return "neutral";
}

function sourceLabel(source = {}) {
  const explicit = clean(source?.item_label);
  if (explicit) return explicit;

  return clean(source?.id || source?.type || "OASYS source")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function sourceDetail(source = {}) {
  const represented = Number(source?.item_count);
  const records = Number(source?.records);

  if (Number.isFinite(represented) && represented > 0) {
    return `${represented} represented`;
  }

  if (Number.isFinite(records) && records > 0) {
    return `${records} loaded`;
  }

  return "Grounded";
}

export default function ValidationIntelligencePanel({
  question = "What needs attention now?",
  card = null,
  metadata = null,
  loading = false,
  error = "",
  onAsk,
}) {
  const [detailsOpen, setDetailsOpen] = useState(false);

  const recommendations = useMemo(
    () => normalizeRows(card?.recommended_actions).filter(Boolean).slice(0, 6),
    [card]
  );

  const evidence = useMemo(
    () =>
      normalizeRows(card?.evidence_used)
        .filter((item) => item && typeof item === "object")
        .slice(0, 12),
    [card]
  );

  const limitations = useMemo(
    () => normalizeRows(card?.known_limitations).filter(Boolean),
    [card]
  );

  const auditPersisted =
    metadata?.audit_persisted ??
    card?.metadata?.audit_persisted ??
    null;

  const hasResponse = Boolean(
    clean(card?.summary) ||
      clean(card?.reasoning) ||
      recommendations.length ||
      evidence.length
  );

  const handleAsk = (prompt) => {
    if (loading || typeof onAsk !== "function") return;
    onAsk(prompt);
  };

  return (
    <section
      className="validation-intelligence-panel"
      aria-labelledby="validation-intelligence-panel-title"
    >
      <div className="validation-intelligence-panel__header">
        <div className="validation-intelligence-panel__identity">
          <span className="validation-intelligence-panel__eyebrow">
            OASYS Intelligence
          </span>
          <h2 id="validation-intelligence-panel-title">
            Current validation intelligence
          </h2>
          <p>
            Grounded, advisory reasoning across validation health, failure pressure,
            evidence coverage, confidence, and closure readiness.
          </p>
        </div>

        <div className="validation-intelligence-panel__status">
          <StatusChip
            tone={confidenceTone(card?.confidence)}
            dot={hasResponse}
          >
            {hasResponse
              ? formatConfidence(card?.confidence)
              : "Awaiting context"}
          </StatusChip>

          {auditPersisted === true ? (
            <StatusChip tone="success">Audited</StatusChip>
          ) : null}

          <Button
            variant="ghost"
            size="small"
            onClick={() => setDetailsOpen((current) => !current)}
            aria-expanded={detailsOpen}
            className="validation-intelligence-panel__details-toggle"
          >
            {detailsOpen ? "Hide details" : "Details"}
          </Button>
        </div>
      </div>

      <div className="validation-intelligence-panel__prompts">
        {PROMPTS.map((prompt) => {
          const isActive = question === prompt;

          return (
            <Button
              key={prompt}
              variant={isActive ? "secondary" : "ghost"}
              size="small"
              disabled={loading}
              aria-pressed={isActive}
              onClick={() => handleAsk(prompt)}
              className={`validation-intelligence-panel__prompt${
                isActive ? " is-active" : ""
              }`}
            >
              {prompt}
            </Button>
          );
        })}
      </div>

      {error ? (
        <div className="validation-intelligence-panel__error" role="alert">
          <strong>Intelligence unavailable</strong>
          <span>{error}</span>
        </div>
      ) : null}

      {hasResponse ? (
        <div
          className="validation-intelligence-panel__body"
          aria-busy={loading}
        >
          <div className="validation-intelligence-panel__reasoning">
            <span className="validation-intelligence-panel__label">
              Current reasoning
            </span>
            <strong>
              {clean(card?.summary) || "Validation context loaded."}
            </strong>
            {clean(card?.reasoning) ? <p>{card.reasoning}</p> : null}
          </div>

          {recommendations.length > 0 ? (
            <div className="validation-intelligence-panel__actions">
              <span className="validation-intelligence-panel__label">
                Recommended operator focus
              </span>
              <ol>
                {recommendations.map((action, index) => (
                  <li key={`${clean(action)}-${index}`}>
                    <span>{index + 1}</span>
                    <p>{clean(action)}</p>
                  </li>
                ))}
              </ol>
            </div>
          ) : null}
        </div>
      ) : (
        <div
          className="validation-intelligence-panel__initial"
          aria-busy={loading}
        >
          <strong>Preparing current validation intelligence.</strong>
          <span>
            OASYS is reading validation health, failed-result pressure, evidence
            coverage, confidence, recent activity, and aggregate closure readiness.
          </span>
        </div>
      )}

      {detailsOpen ? (
        <div className="validation-intelligence-panel__details">
          <div>
            <span className="validation-intelligence-panel__label">
              Grounding
            </span>

            {evidence.length > 0 ? (
              <div className="validation-intelligence-panel__sources">
                {evidence.map((source, index) => (
                  <div
                    key={`${clean(source?.type || source?.id) || "source"}-${index}`}
                    className="validation-intelligence-panel__source"
                  >
                    <strong>{sourceLabel(source)}</strong>
                    <span>{sourceDetail(source)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="validation-intelligence-panel__muted">
                Supporting OASYS validation records are attached to the current response.
              </p>
            )}
          </div>

          <div>
            <span className="validation-intelligence-panel__label">
              Known limitations
            </span>

            {limitations.length > 0 ? (
              <ul className="validation-intelligence-panel__limitations">
                {limitations.map((limitation, index) => (
                  <li key={`${clean(limitation)}-${index}`}>
                    {clean(limitation)}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="validation-intelligence-panel__muted">
                No additional limitations were reported.
              </p>
            )}
          </div>
        </div>
      ) : null}
    </section>
  );
}
